import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertCircle, AlertTriangle, Info, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OperationNotificationBanner = ({ operationId, userId, onActionComplete }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (operationId && userId) {
      fetchOperationNotifications();
    }
  }, [operationId, userId]);

  const fetchOperationNotifications = async () => {
    try {
      // Fetch all unread notifications for this operation
      const response = await axios.get(
        `${API}/notifications?user_id=${userId}&is_read=false`
      );
      
      // Filter for this operation and priority notifications only
      const operationNotifs = response.data.filter(
        n => n.operationId === operationId && 
             (n.notificationType === 'ACTION_REQUISE' || n.notificationType === 'ALERTE')
      );
      
      setNotifications(operationNotifs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await axios.put(`${API}/notifications/${notification.id}/read`);
      
      // Navigate if there's an action URL
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
      
      // Callback to parent
      if (onActionComplete) {
        onActionComplete();
      }
      
      // Remove from list
      setNotifications(notifications.filter(n => n.id !== notification.id));
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ACTION_REQUISE':
        return <AlertCircle className="w-5 h-5" />;
      case 'ALERTE':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'ACTION_REQUISE':
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          iconColor: 'text-red-600',
          badgeBg: 'bg-red-100 text-red-800'
        };
      case 'ALERTE':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-300',
          iconColor: 'text-orange-600',
          badgeBg: 'bg-orange-100 text-orange-800'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          iconColor: 'text-blue-600',
          badgeBg: 'bg-blue-100 text-blue-800'
        };
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'DEVIS': 'Devis',
      'LOGISTIQUE': 'Logistique',
      'TERRAIN': 'Terrain',
      'FINANCE': 'Finance',
      'SYSTEME': 'Système'
    };
    return labels[category] || category;
  };

  if (loading || notifications.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {notifications.map((notification) => {
        const style = getNotificationStyle(notification.notificationType);
        
        return (
          <Card
            key={notification.id}
            className={`${style.bg} ${style.border} border-l-4 cursor-pointer hover:shadow-md transition-all`}
            onClick={() => handleNotificationClick(notification)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`${style.iconColor} mt-0.5`}>
                  {getNotificationIcon(notification.notificationType)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Badge className={`${style.badgeBg} border-0 text-xs`}>
                      {getCategoryLabel(notification.category)}
                    </Badge>
                    <button
                      onClick={(e) => dismissNotification(notification.id, e)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {notification.message}
                  </p>
                  
                  {notification.notificationType === 'ACTION_REQUISE' && (
                    <Button
                      size="sm"
                      className="mt-2 bg-[#1db584] hover:bg-[#16a075] text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(notification);
                      }}
                    >
                      Agir maintenant
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default OperationNotificationBanner;
